import { z } from "zod";

// Enum definitions for invoice types
export enum InvoiceFrequency {
  ONE_TIME = "one-time",
  SCHEDULED = "scheduled",
  RECURRING = "recurring",
}

export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PENDING = "pending",
  PROCESSING = "processing",
  PAID = "paid",
  VOID = "void",
  UNPAID = "unpaid",
  PAST_DUE = "past_due",
  OVERDUE = "overdue",
  UNPAID_AND_OVERDUE = "unpaid_and_overdue",
  FAILED = "failed",
  CANCELLED = "cancelled",
  REVERSED = "reversed",
}

export enum InvoiceDeliveryMethod {
  EMAIL = "email",
  SMS = "sms",
  ALL = "all",
  NONE = "none",
  BOTH = "both",
}

export enum InvoicePaymentTerms {
  NET_15 = "net-15",
  NET_30 = "net-30",
  NET_45 = "net-45",
  NET_60 = "net-60",
  NET_90 = "net-90",
  DUE_ON_ISSUE = "due-on-issue",
  CASH_ON_DELIVERY = "cash-on-delivery",
  OTHER = "other",
  NONE = "none",
}

export enum InvoicePaymentMethod {
  MOBILE_DEPOSIT = "mobile_deposit",
  NONE = "none",
  ACH = "ach",
  BOOK = "book",
}

// Base schemas
export const IntegrationsSchema = z.object({
  quickbooks: z.string().optional(),
  xero: z.string().optional(),
});

export const InvoiceLineItemSchema = z.object({
  description: z.string(),
  quantity: z.number().int().positive(),
  unit_price: z.number().positive(),
  item_total: z.number().positive(),
});

export const InvoiceFromSchema = z.object({
  business_id: z.string().optional(),
  user_id: z.string().optional(),
});

export const InvoiceToSchema = z.object({
  business_id: z.string().optional(),
  contact_id: z.string().optional(),
  contact_ref_id: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().email(),
  phone: z.string().optional(),
  business_name: z.string().optional(),
  business_dba: z.string().optional().nullable(),
  business_address: z.string().optional(),
  business_city: z.string().optional(),
  business_state: z.string().optional(),
  business_zip: z.string().optional(),
});

// CreateInvoiceInSchema
export const CreateInvoicePayloadSchema = z.object({
  invoice_from: InvoiceFromSchema.optional().nullable(),
  invoice_to: InvoiceToSchema,
  invoice_frequency: z.nativeEnum(InvoiceFrequency),
  invoice_frequency_interval: z.number().int().optional().nullable(),
  invoice_due_date: z.string().datetime({ offset: true }).nullable(),
  invoice_send_date: z.string().datetime({ offset: true }).nullable(),
  invoice_line_items: z.array(InvoiceLineItemSchema),
  invoice_subtotal: z.number(),
  invoice_tax_percentage: z.number().optional().nullable(),
  invoice_tax_amount: z.number(),
  // invoice_pass_processing_fee: z.boolean(),
  // invoice_processing_fee: z.number().optional().nullable(),
  invoice_add_paypal: z.boolean().default(false),
  allow_credit_card_payment: z.boolean().optional(),
  invoice_shipping_fee: z.number().optional().nullable(),
  invoice_total: z.number(),
  // invoice_status: z.nativeEnum(InvoiceStatus).default(InvoiceStatus.DRAFT),
  invoice_notes: z.string().optional().nullable(),
  invoice_delivery_method: z
    .nativeEnum(InvoiceDeliveryMethod)
    .optional()
    .nullable(),
  invoice_payment_terms: z
    .nativeEnum(InvoicePaymentTerms)
    .optional()
    .nullable(),
  invoice_number: z.string().optional().nullable(),
  invoice_external_id: z.string().optional().nullable(),
  invoice_external_id_link: z.string().optional().nullable(),
});

export type InvoiceTo = z.infer<typeof InvoiceToSchema>;
export type CreateInvoicePayload = z.infer<typeof CreateInvoicePayloadSchema>;

export const CreateInvoiceResponseSchema = z.object({
  user_id: z.string(),
  invoice_id: z.string(),
  status: z.string(),
  invoice_number: z.string().optional().nullable(),
});

export type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;

export const CalculateInvoiceFeeResponseSchema = z.object({
  formatted_fee: z.string().min(3),
  fee: z.number().nonnegative("Fee must be a positive float number"),
});

export const CalculateInvoiceFeePayloadSchema = z.object({
  invoice_line_items: z.array(InvoiceLineItemSchema),
  invoice_subtotal: z.number().positive(),
});

export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;
export type CalculateInvoiceFeeResponse = z.infer<
  typeof CalculateInvoiceFeeResponseSchema
>;
export type CalculateInvoiceFeePayload = z.infer<
  typeof CalculateInvoiceFeePayloadSchema
>;

export const bulkInvoiceUploadStatusSchema = z.enum(
  ["pending", "success", "failed", "rejected", "processing", "error"],
  {
    message:
      "Upload status must be 'pending', 'success', 'failed', 'rejected', 'error' or 'processing'",
  }
);

// Schema for a single bulk invoice upload entity
export const bulkInvoiceUploadEntitySchema = z.object({
  original_file_name: z.string().min(1, "Original file name is required"),
  failed_file_name: z.string().nullable(),
  upload_status: bulkInvoiceUploadStatusSchema,
  total_records: z
    .number()
    .int()
    .nonnegative("Total records must be a non-negative integer"),
  failed_records: z
    .number()
    .int()
    .nonnegative("Failed records must be a non-negative integer"),
  created_by_name: z.string().min(1, "Creator name is required"),
  user_id: z.string().min(1, "User ID is required"),
  bulk_upload_id: z.string().min(1, "Bulk upload ID is required"),
  created_at: z.string().datetime({
    offset: true,
    message: "Created at must be a valid ISO date string",
  }),
});

// Schema for the list of bulk invoice upload entities
export const bulkInvoiceUploadEntitiesSchema = z.array(
  bulkInvoiceUploadEntitySchema
);

// Schema for pre-signed URL response
export const preSignedUrlSchema = z.string().url("Must be a valid URL");

// Types

export type BulkInvoiceUploadStatus = z.infer<
  typeof bulkInvoiceUploadStatusSchema
>;
export type BulkInvoiceUploadEntity = z.infer<
  typeof bulkInvoiceUploadEntitySchema
>;
export type BulkInvoiceUploadEntities = z.infer<
  typeof bulkInvoiceUploadEntitiesSchema
>;
export type PreSignedUrl = z.infer<typeof preSignedUrlSchema>;
export type DownloadFileType = "template" | "original" | "failed";

// ── Recurring Invoices ──────────────────────────────────────────────

export enum RecurringInvoiceStatus {
  ACTIVE = "active",
  PAUSED = "paused",
  CANCELLED = "cancelled",
}

export enum RecurringInvoiceIntervalDays {
  ONE = 1,
  SEVEN = 7,
  FOURTEEN = 14,
  THIRTY = 30,
  SIXTY = 60,
  NINETY = 90,
}

// Response schema — mirrors backend GetRecurringInvoiceOutSchema
export const RecurringInvoiceScheduleSchema = z.object({
  recurring_invoice_id: z.string(),
  user_id: z.string(),
  status: z.nativeEnum(RecurringInvoiceStatus),
  interval_days: z.number(),
  start_date: z.string(),
  next_send_date: z.string(),
  last_sent_date: z.string().nullable().optional(),
  current_instance_id: z.string().nullable().optional(),
  series_count: z.number(),
  template: z.record(z.unknown()),
  active: z.boolean(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type RecurringInvoiceSchedule = z.infer<
  typeof RecurringInvoiceScheduleSchema
>;

// Create payload — mirrors backend CreateRecurringInvoiceInSchema
export const CreateRecurringInvoicePayloadSchema = z.object({
  interval_days: z.nativeEnum(RecurringInvoiceIntervalDays),
  start_date: z.string().datetime({ offset: true }).nullable().optional(),
  invoice_delivery_method: z.nativeEnum(InvoiceDeliveryMethod),
  invoice_to: InvoiceToSchema,
  invoice_from: InvoiceFromSchema.nullable().optional(),
  invoice_line_items: z.array(InvoiceLineItemSchema),
  invoice_subtotal: z.number(),
  invoice_tax_percentage: z.number().nullable().optional(),
  invoice_tax_amount: z.number().nullable().optional(),
  invoice_pass_processing_fee: z.boolean().default(false),
  invoice_processing_fee: z.number().nullable().optional(),
  invoice_add_paypal: z.boolean().default(false),
  allow_credit_card_payment: z.boolean().optional(),
  invoice_shipping_fee: z.number().nullable().optional(),
  invoice_total: z.number(),
  invoice_notes: z.string().nullable().optional(),
  invoice_payment_terms: z
    .nativeEnum(InvoicePaymentTerms)
    .nullable()
    .optional(),
  invoice_number: z.string().nullable().optional(),
});

export type CreateRecurringInvoicePayload = z.infer<
  typeof CreateRecurringInvoicePayloadSchema
>;
